import { CommonModule } from '@angular/common';
import { Component, OnDestroy, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { ChatConversation } from './documentation-chat';
import { BackendApiService } from '../../../services/backend-api.service';
import { HttpParams } from '@angular/common/http';
import { ChatWindowComponent } from './chat-window/chat-window.component';
import { NotificationService } from '../../../services/notification.service';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { DialogService } from '../../../services/dialog.service';
import { finalize, Subscription } from 'rxjs';

@Component({
    selector: 'phito-documentation-chat-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogTitle,
        MatDialogContent,
        MatIconModule,
        MatButton,
        ChatWindowComponent,
        MatIconButton,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
    ],
    templateUrl: './documentation-chat-dialog.component.html',
    styleUrls: ['./documentation-chat-dialog.component.scss'],
})
export class DocumentationChatDialogComponent implements OnDestroy {
    private api = inject(BackendApiService);
    private notificationService = inject(NotificationService);
    private dialogService = inject<DialogService<ChatConversation>>(DialogService);

    dialogRef = inject<MatDialogRef<DocumentationChatDialogComponent>>(MatDialogRef);

    isLoadingConversations = signal(false);
    isSavingConversation = signal(false);

    conversations: ChatConversation[] = [];
    openedConversations: ChatConversation[] = [];
    activeConversation = signal<ChatConversation | undefined>(undefined);
    private conversationsSubscription?: Subscription;

    constructor() {
        this.loadConversations();
    }

    loadConversations(): void {
        this.isLoadingConversations.set(true);
        this.conversationsSubscription?.unsubscribe();

        let params = new HttpParams().set('orderby', 'createdAt::desc');
        params = params.set('limit', '20');

        this.conversationsSubscription = this.api
            .getAll<ChatConversation>('conversations', params)
            .pipe(finalize(() => this.isLoadingConversations.set(false)))
            .subscribe({
                next: (response) => {
                    this.conversations = response.data;
                    if (this.conversations.length > 0) {
                        this.openedConversations = [this.conversations[0]];
                        this.activeConversation.set(this.conversations[0]);
                    } else {
                        this.startNewConversation();
                    }
                },
                error: (error) => {
                    this.notificationService.errorMessageGet('Fehler beim Laden der Konversationen!', error);
                },
            });
    }

    startNewConversation(): void {
        this.isSavingConversation.set(true);
        this.api
            .create<ChatConversation>('conversations', new ChatConversation())
            .pipe(finalize(() => this.isSavingConversation.set(false)))
            .subscribe({
                next: (response) => {
                    this.conversations = [response, ...this.conversations];
                    this.selectConversation(response);
                },
                error: (error) => {
                    this.notificationService.errorMessagePost(
                        'Fehler beim Erstellen der Konversation!',
                        error,
                        null,
                    );
                },
            });
    }

    selectConversation(conversation: ChatConversation): void {
        if (!this.openedConversations.some((conversation1) => conversation1.id === conversation.id)) {
            this.openedConversations = [conversation, ...this.openedConversations];
        }
        this.activeConversation.set(conversation);
    }

    renameConversation(conversation: ChatConversation): void {
        this.dialogService
            .enterStringValue('Unterhaltung umbenennen', {
                currentValue: conversation.name || '',
                placeholder: 'Name',
                buttonText: 'Speichern',
            })
            .subscribe({
                next: (result) => {
                    if (!result?.isConfirmed()) {
                        return;
                    }
                    const newName = result.getEntity()?.value?.trim();
                    if (!newName || newName === conversation.name) {
                        return;
                    }
                    const previousName = conversation.name;
                    conversation.name = newName;
                    this.isSavingConversation.set(true);
                    this.api
                        .update<ChatConversation>('conversations', conversation)
                        .pipe(finalize(() => this.isSavingConversation.set(false)))
                        .subscribe({
                            next: (response) => {
                                conversation.name = response?.name || newName;
                            },
                            error: (error) => {
                                conversation.name = previousName;
                                this.notificationService.errorMessagePut(
                                    'Fehler beim Umbenennen der Konversation!',
                                    error,
                                    conversation,
                                );
                            },
                        });
                },
            });
    }

    deleteConversation(conversation: ChatConversation): void {
        this.dialogService
            .confirm('Unterhaltung löschen', 'Möchten Sie diese Unterhaltung wirklich löschen?')
            .subscribe({
                next: (result) => {
                    if (!result?.isConfirmed()) {
                        return;
                    }
                    this.api.delete('conversations', conversation.id).subscribe({
                        next: () => {
                            this.conversations = this.conversations.filter(
                                (conversation1) => conversation1.id !== conversation.id,
                            );
                            this.openedConversations = this.openedConversations.filter(
                                (conversation1) => conversation1.id !== conversation.id,
                            );
                            if (this.activeConversation()?.id === conversation.id) {
                                const nextConversation = this.openedConversations[0] || this.conversations[0];
                                if (nextConversation) {
                                    this.activeConversation.set(nextConversation);
                                } else {
                                    this.activeConversation.set(undefined);
                                    this.startNewConversation();
                                }
                            }
                        },
                        error: (error) => {
                            this.notificationService.errorMessageDelete(
                                'Fehler beim Löschen der Konversation!',
                                error,
                            );
                        },
                    });
                },
            });
    }

    ngOnDestroy(): void {
        this.conversationsSubscription?.unsubscribe();
    }
}
